import axios from '@config/axios-config';
import { IApiResponse } from '@datatype/common/api-type';
import {
  IAdminClozeData,
  IAdminClozeMetricData,
  IAdminDailyMetricData,
  IAdminExpressionData,
  IAdminExpressionMetricData,
  IAdminOverviewMetricData,
  IAdminSourceData,
  IAdminStudentMetricData,
  IAdminVocabularyBookData,
  ICreateExpressionBatchRequest,
  IModifyExpressionRequest,
  IReadAdminClozesRequest,
  IReadAdminDailyMetricsRequest,
  IReadAdminExpressionsRequest,
  IReadAdminSourcesRequest,
  IReadAdminStudentMetricsRequest,
  IReadAdminVocabularyBooksRequest,
} from '@datatype/admin/api-type';

// ###################################
// admin authentic expression
// ###################################
export const getAdminExpressions = async (
  request: IReadAdminExpressionsRequest,
): Promise<IApiResponse<IAdminExpressionData[]>> => {
  const { page, perPage, languageCode, contentType, isPublished, isChecked } = request;
  const params: Record<string, string | number | boolean> = { page, perPage };
  if (languageCode) params.languageCode = languageCode;
  if (contentType) params.contentType = contentType;
  if (isPublished != null) params.isPublished = isPublished;
  if (isChecked != null) params.isChecked = isChecked;

  const response = await axios
    .get<IApiResponse<IAdminExpressionData[]>>(`/api/v1/admin/ig/expression`, { params })
    .then((res) => res.data);
  return response;
};

export const getAdminExpression = async (expressionKey: string): Promise<IApiResponse<IAdminExpressionData>> => {
  const response = await axios
    .get<IApiResponse<IAdminExpressionData>>(`/api/v1/admin/ig/expression/${expressionKey}`)
    .then((res) => res.data);
  return response;
};

export const createExpressionBatch = async (request: ICreateExpressionBatchRequest): Promise<IApiResponse<void>> => {
  const response = await axios
    .post<IApiResponse<void>>(`/api/v1/ig/expression/create/batch`, request)
    .then((res) => res.data);
  return response;
};

export const modifyExpression = async (
  request: IModifyExpressionRequest,
): Promise<IApiResponse<IAdminExpressionData>> => {
  const { expressionKey, ...body } = request;
  const response = await axios
    .patch<IApiResponse<IAdminExpressionData>>(`/api/v1/admin/ig/expression/${expressionKey}`, body)
    .then((res) => res.data);
  return response;
};

export const deleteExpression = async (expressionKey: string): Promise<IApiResponse<void>> => {
  const response = await axios
    .delete<IApiResponse<void>>(`/api/v1/admin/ig/expression/${expressionKey}`)
    .then((res) => res.data);
  return response;
};

// ###################################
// admin authentic expression source
// ###################################
export const getAdminSources = async (
  request: IReadAdminSourcesRequest,
): Promise<IApiResponse<IAdminSourceData[]>> => {
  const { page, perPage, languageCode, sortBy, sortOrder } = request;
  const params: Record<string, string | number> = { page, perPage };
  if (languageCode) params.languageCode = languageCode;
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;

  const response = await axios
    .get<IApiResponse<IAdminSourceData[]>>(`/api/v1/admin/ig/expression-source`, { params })
    .then((res) => res.data);
  return response;
};

export const getAdminSource = async (key: string): Promise<IApiResponse<IAdminSourceData>> => {
  const response = await axios
    .get<IApiResponse<IAdminSourceData>>(`/api/v1/admin/ig/expression-source/${key}`)
    .then((res) => res.data);
  return response;
};

export const deleteAdminSource = async (key: string): Promise<IApiResponse<void>> => {
  const response = await axios
    .delete<IApiResponse<void>>(`/api/v1/admin/ig/expression-source/${key}`)
    .then((res) => res.data);
  return response;
};

// ###################################
// admin cloze
// ###################################
export const getAdminClozes = async (
  request: IReadAdminClozesRequest,
): Promise<IApiResponse<IAdminClozeData[]>> => {
  const { page, perPage, clozeType, languageCode } = request;
  const params: Record<string, string | number> = { page, perPage };
  if (clozeType) params.clozeType = clozeType;
  if (languageCode) params.languageCode = languageCode;

  const response = await axios
    .get<IApiResponse<IAdminClozeData[]>>(`/api/v1/admin/ig/cloze`, { params })
    .then((res) => res.data);
  return response;
};

// ###################################
// admin metric
// ###################################
export const getAdminStudentMetrics = async (
  request: IReadAdminStudentMetricsRequest,
): Promise<IApiResponse<IAdminStudentMetricData[]>> => {
  const { date, page, perPage, studied } = request;
  const params: Record<string, string | number | boolean> = { date, page, perPage };
  if (studied != null) params.studied = studied;
  const response = await axios
    .get<IApiResponse<IAdminStudentMetricData[]>>(`/api/v1/admin/metric/student`, { params })
    .then((res) => res.data);
  return response;
};

export const getAdminDailyMetrics = async (
  request: IReadAdminDailyMetricsRequest,
): Promise<IApiResponse<IAdminDailyMetricData[]>> => {
  const { page, perPage } = request;
  const params: Record<string, string | number> = { page, perPage };
  const response = await axios
    .get<IApiResponse<IAdminDailyMetricData[]>>(`/api/v1/admin/metric/daily`, { params })
    .then((res) => res.data);
  return response;
};

export const getAdminOverviewMetric = async (): Promise<IApiResponse<IAdminOverviewMetricData>> => {
  const response = await axios
    .get<IApiResponse<IAdminOverviewMetricData>>(`/api/v1/admin/metric/overview`)
    .then((res) => res.data);
  return response;
};

export const getAdminExpressionMetric = async (): Promise<IApiResponse<IAdminExpressionMetricData>> => {
  const response = await axios
    .get<IApiResponse<IAdminExpressionMetricData>>(`/api/v1/admin/metric/expression`)
    .then((res) => res.data);
  return response;
};

export const getAdminClozeMetric = async (): Promise<IApiResponse<IAdminClozeMetricData>> => {
  const response = await axios
    .get<IApiResponse<IAdminClozeMetricData>>(`/api/v1/admin/metric/cloze`)
    .then((res) => res.data);
  return response;
};

// ###################################
// admin vocabulary book
// ###################################
export const getAdminVocabularyBooks = async (
  request: IReadAdminVocabularyBooksRequest,
): Promise<IApiResponse<IAdminVocabularyBookData[]>> => {
  const { page, perPage, languageCode, category, levels, published } = request;
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('perPage', String(perPage));
  if (languageCode) params.append('languageCode', languageCode);
  if (category) params.append('category', category);
  if (levels && levels.length > 0) {
    levels.forEach((lv) => params.append('levels', lv));
  }
  if (published != null) params.append('published', String(published));

  const response = await axios
    .get<IApiResponse<IAdminVocabularyBookData[]>>(`/api/v1/admin/ig/vocabulary-book?${params.toString()}`)
    .then((res) => res.data);
  return response;
};

export const assembleAdminVocabularyBook = async (): Promise<IApiResponse<void>> => {
  const response = await axios
    .post<IApiResponse<void>>(`/api/v1/admin/ig/vocabulary-book/assemble`)
    .then((res) => res.data);
  return response;
};
